import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { Button, Segmented, Spin, Typography, App } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useCurrency } from '../hooks/useCurrency';
import { setSelectedCategory } from '../store/slices/productsSlice';
import {
  useGetCategoriesQuery,
  useGetProductsQuery,
  useGetProductsByCategoryQuery,
} from '../services/productsApi';
import {
  useCreateTakeOutOrderMutation,
  useCreateDeliveryOrderMutation,
  useCreateInternalConsumptionOrderMutation,
  useGetOrderQuery,
  useUpdateOrderMutation,
} from '../services/ordersApi';
import { useResendKitchenOrder } from '../hooks/useResendKitchenOrder';
import { useGetBoolSettingQuery } from '../services/settingsApi';
import { useGetActiveDailyMenusQuery } from '../services/dailyMenuApi';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import { Product, ProductOrderItem } from '../types/products';
import { Customer, CustomerAddress } from '../types/customer';
import DailyMenuSelectionModal from '../components/orders/modals/DailyMenuSelectionModal';
import ProductVariantsModal from '../components/orders/modals/ProductVariantsModal';
import ResendOrderModal from '../components/orders/modals/ResendOrderModal';
import ProductsPanel from '../components/orders/orderTaking/ProductsPanel';
import OrderCartPanel from '../components/orders/orderTaking/OrderCartPanel';
import TakeOutCustomerStrip from '../components/orders/orderTaking/TakeOutCustomerStrip';
import DeliveryCustomerStrip from '../components/orders/orderTaking/DeliveryCustomerStrip';
import InternalConsumptionStrip from '../components/orders/orderTaking/InternalConsumptionStrip';
import { useKitchenPipeline } from '../components/orders/orderTaking/useKitchenPipeline';
import { newClientId } from '../utils/clientId';
import { getErrorMessage, getRetryableWriteErrorMessage } from '../utils/errorHelpers';
import '../components/orders/orderTaking/orderTaking.css';

const { Text, Title } = Typography;

type OrderTakingType = 'takeout' | 'delivery' | 'internal';

const TYPE_CONFIG: Record<OrderTakingType, { areaName: string; label: string }> = {
  takeout: { areaName: 'Para Llevar', label: '🥡 Para llevar' },
  delivery: { areaName: 'Domicilio', label: '🛵 Domicilio' },
  internal: { areaName: 'Consumo Interno', label: '🍴 Consumo interno' },
};

/**
 * Shared order-taking page for TakeOut and Delivery. The type is a route param
 * (/new-order/:type) rendered by ONE route, so switching type keeps the cart —
 * and the old chooser page is gone. Replaces TakeOutOrder + DeliveryOrder,
 * which duplicated the products panel, cart and kitchen pipeline line-for-line.
 */
const OrderTaking: React.FC = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const { isMobile } = useResponsive();
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const { currencySymbol } = useCurrency();

  const editOrderId = searchParams.get('edit');
  const isEditMode = !!editOrderId;

  // Cart + capture state
  const [localOrderItems, setLocalOrderItems] = useState<ProductOrderItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editLoaded, setEditLoaded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [dailyMenuModalVisible, setDailyMenuModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [editingDailyMenu, setEditingDailyMenu] = useState(false);
  const [variantsModalVisible, setVariantsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  // Ids del alta que aún no se confirma (orden y renglones). Se conservan entre intentos para
  // que el servidor reconozca el reintento, y es lo que deja tomarla sin conexión: la cola
  // la manda después con estos mismos Ids. Ver ComandearModal.
  const pendingCreateRef = useRef<{ orderId: string; cartKey: string; itemIds: string[] } | null>(null);

  // Customer state (shared) + delivery-only extras
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<CustomerAddress | null>(null);
  const [saveCustomer, setSaveCustomer] = useState(true);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const { selectedCategoryId } = useAppSelector((state) => state.products);

  // Queries
  const { data: categories = [], isLoading: categoriesLoading } = useGetCategoriesQuery({ activeOnly: true });
  const { data: allProducts = [], isLoading: allProductsLoading } = useGetProductsQuery({ availableOnly: true });
  const { data: categoryProducts = [], isLoading: categoryProductsLoading } =
    useGetProductsByCategoryQuery(selectedCategoryId || '', { skip: !selectedCategoryId });

  const products = searchQuery.trim()
    ? allProducts
    : (selectedCategoryId ? categoryProducts : allProducts);
  const productsLoading = searchQuery.trim()
    ? allProductsLoading
    : (selectedCategoryId ? categoryProductsLoading : allProductsLoading);

  const { data: menuDelDiaSetting } = useGetBoolSettingQuery('MenuDelDiaActivo');
  const { data: activeDailyMenus = [] } = useGetActiveDailyMenusQuery(undefined, {
    skip: !menuDelDiaSetting?.value,
  });
  const hasActiveDailyMenu = (menuDelDiaSetting?.value ?? false) && activeDailyMenus.length > 0;

  // Edit mode: fetch existing order
  const { data: existingOrder, isLoading: orderLoading } = useGetOrderQuery(editOrderId || '', {
    skip: !editOrderId,
  });

  // Mutations + kitchen/print pipeline
  const [createTakeOutOrder, { isLoading: isCreatingTakeOut }] = useCreateTakeOutOrderMutation();
  const [createDeliveryOrder, { isLoading: isCreatingDelivery }] = useCreateDeliveryOrderMutation();
  const [createInternalConsumptionOrder, { isLoading: isCreatingInternal }] = useCreateInternalConsumptionOrderMutation();
  const [updateOrder, { isLoading: isUpdatingOrder }] = useUpdateOrderMutation();
  const pipeline = useKitchenPipeline();

  useEffect(() => {
    dispatch(setSelectedCategory(null));
  }, [dispatch]);

  // Populate state in edit mode
  useEffect(() => {
    if (isEditMode && existingOrder && !editLoaded) {
      setCustomerName(existingOrder.customerName || '');
      setCustomerPhone(existingOrder.customerPhone || '');
      if (existingOrder.deliveryNotes) setDeliveryNotes(existingOrder.deliveryNotes);
      // Internal orders store their motivo in Order.notes
      if (existingOrder.isInternalConsumption && existingOrder.notes) setInternalNotes(existingOrder.notes);

      const items: ProductOrderItem[] = (existingOrder.orderItems || [])
        .filter(item => !item.isCancelled)
        .map(item => ({
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          notes: item.notes,
          existingOrderItemId: item.id,
          selectedVariants: (item.variants || []).map(v => ({
            id: v.variantId,
            name: v.variantName,
            priceModifier: v.priceModifier,
          })),
          variantSelections: (item.variants || []).some(v => v.quantity != null)
            ? (item.variants || []).map(v => ({
                variantId: v.variantId,
                name: v.variantName,
                priceModifier: v.priceModifier,
                quantity: v.quantity,
              }))
            : undefined,
          dailyMenuId: item.dailyMenuId,
          dailyMenuName: item.dailyMenuName,
          dailyMenuSelections: item.dailyMenuSelections?.map(s => ({
            courseId: s.dailyMenuCourseId,
            courseName: s.courseName,
            optionId: s.dailyMenuOptionId,
            optionName: s.optionName,
            priceModifier: s.priceModifier,
          })),
        }));

      setLocalOrderItems(items);
      setEditLoaded(true);
    }
  }, [isEditMode, existingOrder, editLoaded]);

  const { confirmDiscard } = useUnsavedChangesWarning({
    hasUnsavedChanges: localOrderItems.some(i => !i.existingOrderItemId),
  });

  const {
    resendModalVisible,
    openResendModal,
    closeResendModal,
    handleResendConfirm,
    printing,
  } = useResendKitchenOrder(
    existingOrder,
    `${TYPE_CONFIG[(type as OrderTakingType) || 'takeout']?.areaName || 'Para Llevar'} - ${existingOrder?.customerName || ''}`
  );

  // Route guard AFTER all hooks so the hook order stays stable
  if (type !== 'takeout' && type !== 'delivery' && type !== 'internal') {
    return <Navigate to="/new-order/takeout" replace />;
  }
  const orderType = type as OrderTakingType;
  const isDelivery = orderType === 'delivery';
  const isInternal = orderType === 'internal';
  const { areaName } = TYPE_CONFIG[orderType];

  // ----- cart handlers -----
  const handleCategoryChange = (categoryId: string | null) => {
    dispatch(setSelectedCategory(categoryId));
    setSearchQuery('');
  };

  // No toast per tap: the tile badge and the cart already give the feedback
  const handleProductAdd = (orderItem: ProductOrderItem) => {
    setLocalOrderItems((prev) => {
      const canGroup = !orderItem.notes &&
        orderItem.selectedVariants.length === 0 &&
        !orderItem.variantSelections?.length &&
        !orderItem.dailyMenuId;

      if (canGroup) {
        const existingIndex = prev.findIndex(item =>
          !item.existingOrderItemId &&
          item.productId === orderItem.productId &&
          !item.notes &&
          item.selectedVariants.length === 0 &&
          !item.variantSelections?.length &&
          !item.dailyMenuId
        );
        if (existingIndex >= 0) {
          return prev.map((item, i) =>
            i === existingIndex ? { ...item, quantity: item.quantity + orderItem.quantity } : item
          );
        }
      }
      return [...prev, orderItem];
    });
  };

  const handleDailyMenuAdd = (orderItem: ProductOrderItem) => {
    if (editingItemIndex !== null) {
      setLocalOrderItems(prev => prev.map((item, i) => i === editingItemIndex ? orderItem : item));
      clearEditState();
    } else {
      setLocalOrderItems((prev) => [...prev, orderItem]);
    }
  };

  const handleRemoveItem = (index: number) => {
    setLocalOrderItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(index);
      return;
    }
    setLocalOrderItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, quantity: Math.min(99, quantity) } : item))
    );
  };

  const clearEditState = () => {
    setEditingItemIndex(null);
    setEditingDailyMenu(false);
  };

  const handleEditItem = (index: number) => {
    const item = localOrderItems[index];
    if (item.dailyMenuId) {
      setEditingItemIndex(index);
      setEditingDailyMenu(true);
    } else {
      const product = allProducts?.find(p => p.id === item.productId);
      if (product) {
        setEditingItemIndex(index);
        setSelectedProduct(product);
        setVariantsModalVisible(true);
      } else {
        message.error('No se encontró el producto para editar');
      }
    }
  };

  const handleVariantsSubmit = (orderItem: ProductOrderItem) => {
    if (editingItemIndex !== null) {
      setLocalOrderItems(prev => prev.map((item, i) => i === editingItemIndex ? orderItem : item));
      clearEditState();
    } else {
      setLocalOrderItems(prev => [...prev, orderItem]);
    }
    setVariantsModalVisible(false);
  };

  const filteredProducts = products.filter(
    (product) =>
      product.isActive && (
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const handleBack = () => {
    confirmDiscard(() => navigate('/orders'));
  };

  const handleTypeChange = (value: OrderTakingType) => {
    // Same route element → the component stays mounted and the cart survives
    navigate(`/new-order/${value}`, { replace: true });
  };

  // ----- submit -----
  const validate = (): string | null => {
    if (localOrderItems.length === 0) return 'Agrega al menos un producto a la orden';
    if (isInternal) {
      if (!customerName.trim()) return 'Indica para quién es el consumo interno';
    } else if (isDelivery) {
      if (!selectedCustomer && (!customerName.trim() || !customerPhone.trim())) {
        return 'Captura el teléfono y nombre del cliente';
      }
      if (!isEditMode && !selectedAddress) return 'Selecciona o captura la dirección de entrega';
    } else {
      // Para llevar: el teléfono es opcional (en mostrador no siempre hay uno)
      if (!customerName.trim()) return 'Captura el nombre del cliente';
    }
    return null;
  };

  const effectiveCustomerName = selectedCustomer?.name || customerName;

  const handleSubmit = async () => {
    if (isSending) return;
    const validationError = validate();
    if (validationError) {
      message.warning(validationError);
      return;
    }

    setIsSending(true);
    try {
      const orderItems = localOrderItems.map((item) => ({
        // The id is what tells the update endpoint "this daily menu is already on the order,
        // keep it" — without it every save recreated the menú as a $0 anchor row and the ones
        // dropped from the cart could never be taken off the ticket.
        id: item.existingOrderItemId,
        productId: item.productId,
        quantity: item.quantity,
        notes: item.notes,
        variantIds: item.selectedVariants.map((v) => v.id),
        variantSelections: item.variantSelections?.map(v => ({ variantId: v.variantId, quantity: v.quantity })),
        dailyMenuId: item.dailyMenuId || undefined,
        dailyMenuSelections: item.dailyMenuSelections?.map(s => ({ courseId: s.courseId, optionId: s.optionId })),
      }));

      const sendParams = { areaName, customerName: effectiveCustomerName };

      if (isEditMode && editOrderId) {
        const newLocalItems = localOrderItems.filter(i => !i.existingOrderItemId);
        const updatedOrder = await updateOrder({ id: editOrderId, orderItems }).unwrap();
        await pipeline.sendEditedOrder(updatedOrder, existingOrder, newLocalItems, sendParams);
        message.success(
          newLocalItems.length > 0
            ? `Orden actualizada (${newLocalItems.length} producto(s) enviado(s) a cocina)`
            : 'Orden actualizada exitosamente'
        );
        navigate('/orders');
        return;
      }

      // La orden conserva su Id mientras no se confirme; los renglones, solo si el carrito no
      // cambió. Si el envío anterior sí se guardó y la cajera editó algo, el servidor rechaza
      // la diferencia en vez de devolver la orden de antes como si la edición hubiera entrado.
      const cartKey = JSON.stringify(orderItems);
      const previous = pendingCreateRef.current;
      const itemIds = previous?.cartKey === cartKey ? previous.itemIds : orderItems.map(() => newClientId());
      const orderId = previous?.orderId ?? newClientId();
      pendingCreateRef.current = { orderId, cartKey, itemIds };
      const newOrderItems = orderItems.map((item, i) => ({ ...item, id: itemIds[i] }));

      let newOrder;
      if (isInternal) {
        newOrder = await createInternalConsumptionOrder({
          personName: customerName,
          notes: internalNotes.trim() || undefined,
          orderItems,
        }).unwrap();
      } else if (isDelivery) {
        const hasValidAddressId = !!selectedAddress?.id;
        newOrder = await createDeliveryOrder({
          id: orderId,
          customerId: selectedCustomer?.id,
          // Con customerId el servidor ignora nombre y teléfono; se mandan igual para que la
          // orden tomada sin conexión tenga a quién imprimir en el ticket.
          customerName: selectedCustomer?.name ?? customerName,
          customerPhone: selectedCustomer?.phone ?? customerPhone,
          deliveryAddressId: hasValidAddressId ? selectedAddress!.id : undefined,
          // Con deliveryAddressId el servidor ignora newAddress; se manda igual para que la
          // orden tomada sin conexión lleve la dirección escrita (ticket y repartidor).
          newAddress: selectedAddress ? {
            label: selectedAddress.label,
            street: selectedAddress.street,
            exteriorNumber: selectedAddress.exteriorNumber || undefined,
            interiorNumber: selectedAddress.interiorNumber || undefined,
            colony: selectedAddress.colony || undefined,
            city: selectedAddress.city || undefined,
            postalCode: selectedAddress.postalCode || undefined,
            reference: selectedAddress.reference || undefined,
          } : undefined,
          deliveryNotes,
          saveCustomer,
          orderItems: newOrderItems,
        }).unwrap();
      } else {
        newOrder = await createTakeOutOrder({
          id: orderId,
          customerName,
          customerPhone: customerPhone.trim() || undefined,
          saveCustomer: false,
          orderItems: newOrderItems,
        }).unwrap();
      }
      // Guardada: la siguiente orden lleva Ids nuevos.
      pendingCreateRef.current = null;

      await pipeline.sendCreatedOrder(newOrder, localOrderItems, sendParams);
      message.success(
        isInternal
          ? 'Consumo interno enviado a cocina'
          : isDelivery
            ? 'Orden a domicilio creada exitosamente'
            : 'Orden para llevar creada exitosamente'
      );
      navigate('/orders');
    } catch (error: any) {
      console.error('Error creating/updating order:', error);
      // Crear lleva Id del cliente y volver a pulsar es seguro; editar es un PUT sin él.
      message.error(isEditMode || isInternal
        ? getErrorMessage(error, 'Error al procesar la orden')
        : getRetryableWriteErrorMessage(error, 'Error al procesar la orden'));
    } finally {
      setIsSending(false);
    }
  };

  const submitting = isSending || isCreatingTakeOut || isCreatingDelivery || isCreatingInternal || isUpdatingOrder;
  const totalItems = localOrderItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = localOrderItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const cartQuantities = localOrderItems.reduce<Record<string, number>>((acc, item) => {
    acc[item.productId] = (acc[item.productId] || 0) + item.quantity;
    return acc;
  }, {});

  if (isEditMode && orderLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}><Text>Cargando orden…</Text></div>
      </div>
    );
  }

  return (
    <div className="ot-page">
      {/* Single bar: back + title + type switch (the old chooser page is gone) */}
      <div className="ot-bar">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} />
        <h2 className="ot-bar__title">{isEditMode ? 'Editar orden' : 'Nueva orden'}</h2>
        <Segmented
          value={orderType}
          disabled={isEditMode}
          onChange={(value) => handleTypeChange(value as OrderTakingType)}
          options={[
            { label: TYPE_CONFIG.takeout.label, value: 'takeout' },
            { label: TYPE_CONFIG.delivery.label, value: 'delivery' },
            { label: TYPE_CONFIG.internal.label, value: 'internal' },
          ]}
        />
        <div className="ot-bar__spacer" />
        {isEditMode && existingOrder && (
          <Button icon={<PrinterOutlined />} onClick={openResendModal} loading={printing}>
            {!isMobile && 'Reenviar Comanda'}
          </Button>
        )}
      </div>

      {/* Customer strip */}
      {isInternal ? (
        <InternalConsumptionStrip
          personName={customerName}
          notes={internalNotes}
          onPersonNameChange={setCustomerName}
          onNotesChange={setInternalNotes}
          disabled={isEditMode}
        />
      ) : isDelivery ? (
        <DeliveryCustomerStrip
          selectedCustomer={selectedCustomer}
          onSelectCustomer={setSelectedCustomer}
          name={customerName}
          onNameChange={setCustomerName}
          phone={customerPhone}
          onPhoneChange={setCustomerPhone}
          selectedAddress={selectedAddress}
          onSelectAddress={setSelectedAddress}
          saveCustomer={saveCustomer}
          onSaveCustomerChange={setSaveCustomer}
          deliveryNotes={deliveryNotes}
          onDeliveryNotesChange={setDeliveryNotes}
          disabled={isEditMode}
        />
      ) : (
        <TakeOutCustomerStrip
          name={customerName}
          phone={customerPhone}
          onNameChange={setCustomerName}
          onPhoneChange={setCustomerPhone}
          disabled={isEditMode}
        />
      )}

      {/* Products + cart */}
      <div className="ot-cols">
        <ProductsPanel
          categories={categories}
          categoriesLoading={categoriesLoading}
          selectedCategoryId={selectedCategoryId}
          onCategoryChange={handleCategoryChange}
          products={filteredProducts}
          productsLoading={productsLoading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          cartQuantities={cartQuantities}
          onAddToOrder={handleProductAdd}
          hasActiveDailyMenu={hasActiveDailyMenu}
          onOpenDailyMenu={() => setDailyMenuModalVisible(true)}
          isMobile={isMobile}
        />
        <OrderCartPanel
          items={localOrderItems}
          customerLabel={effectiveCustomerName || undefined}
          submitLabel={isEditMode ? 'Guardar cambios y enviar a cocina' : 'Crear orden y enviar a cocina'}
          submitting={submitting}
          onSubmit={handleSubmit}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onEditItem={handleEditItem}
        />
      </div>

      {/* Mobile sticky footer */}
      {isMobile && totalItems > 0 && (
        <div className="ot-mobile-footer">
          <div>
            <Text type="secondary">{totalItems} productos</Text>
            <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
              {currencySymbol}{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Title>
          </div>
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            loading={submitting}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            {isEditMode ? 'Guardar Cambios' : 'Crear Orden'}
          </Button>
        </div>
      )}

      {/* Modals */}
      {selectedProduct && (
        <ProductVariantsModal
          visible={variantsModalVisible}
          onCancel={() => { setVariantsModalVisible(false); clearEditState(); }}
          onConfirm={handleVariantsSubmit}
          product={selectedProduct}
          initialValues={editingItemIndex !== null && !editingDailyMenu ? {
            quantity: localOrderItems[editingItemIndex].quantity,
            notes: localOrderItems[editingItemIndex].notes || '',
            variantSelections: localOrderItems[editingItemIndex].variantSelections,
          } : undefined}
        />
      )}

      <DailyMenuSelectionModal
        visible={dailyMenuModalVisible || editingDailyMenu}
        onCancel={() => { setDailyMenuModalVisible(false); setEditingDailyMenu(false); clearEditState(); }}
        onAddItem={handleDailyMenuAdd}
        initialValues={editingItemIndex !== null && editingDailyMenu ? {
          dailyMenuId: localOrderItems[editingItemIndex].dailyMenuId!,
          quantity: 1,
          notes: localOrderItems[editingItemIndex].notes,
          dailyMenuSelections: localOrderItems[editingItemIndex].dailyMenuSelections!,
        } : undefined}
      />

      {existingOrder && (
        <ResendOrderModal
          visible={resendModalVisible}
          onCancel={closeResendModal}
          onConfirm={handleResendConfirm}
          orderId={existingOrder.id}
        />
      )}
    </div>
  );
};

export default OrderTaking;
